/**
 * Supabase Client Configuration for YDM Media Intelligence
 *
 * Configured for SSO across *.ydm.family subdomains
 * Multi-tenant with workspace-based data isolation (RLS on workspace_id)
 */

import { createClient, SupabaseClient, type User as SupabaseAuthUser } from "@supabase/supabase-js";
import type { Database } from "@/types/database.types";
import { warn, logError } from '../services/logger';
import type {
  User,
  AuthSession,
  ServiceResult,
} from "../types";
import type { TFn } from "@/utils/channelCorrelation";

// CRITICAL: Vite only treats `import.meta.env.VITE_X` as a static replacement
// when X is a literal property access. Dynamic indexing like
// `import.meta.env[someKey]` causes Vite to inline the ENTIRE env object into
// the bundle — that's how our Gemini key leaked. Always use named lookups.
const viteEnv =
  typeof import.meta !== "undefined" && import.meta.env ? import.meta.env : undefined;

const SUPABASE_URL =
  viteEnv?.VITE_SUPABASE_URL ??
  (typeof process !== "undefined" ? process.env.VITE_SUPABASE_URL : undefined) ??
  "";

const SUPABASE_ANON_KEY =
  viteEnv?.VITE_SUPABASE_ANON_KEY ??
  (typeof process !== "undefined" ? process.env.VITE_SUPABASE_ANON_KEY : undefined) ??
  "";

// Singleton instance
let supabaseInstance: SupabaseClient<Database> | null = null;

export interface SupabaseClientOptions {
  url?: string;
  anonKey?: string;
}

/**
 * Create or get the Supabase client instance
 */
export const createSupabaseClient = (
  options?: SupabaseClientOptions
): SupabaseClient<Database> => {
  const url = options?.url || SUPABASE_URL;
  const key = options?.anonKey || SUPABASE_ANON_KEY;

  if (!url || !key) {
    warn('Supabase', 'Supabase credentials not configured. Using mock mode.');
  }

  if (!supabaseInstance) {
    // Use placeholder values to avoid SDK throw when credentials are absent
    // (e.g. demo mode). The client won't make successful requests without real creds.
    supabaseInstance = createClient<Database>(url || "http://localhost:54321", key || "placeholder-key-not-configured", {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        flowType: "pkce",
        storage: typeof window !== "undefined" ? window.localStorage : undefined,
      },
    });
  }

  return supabaseInstance;
};

/**
 * Get the current Supabase client
 */
export const getSupabaseClient = (): SupabaseClient<Database> => {
  return supabaseInstance || createSupabaseClient();
};

/**
 * Check if Supabase is configured
 */
export const isSupabaseConfigured = (): boolean => {
  return !!(SUPABASE_URL && SUPABASE_ANON_KEY);
};

/**
 * Map a raw Supabase auth user onto the app's `User` shape.
 *
 * Pure and synchronous — safe to call from inside an `onAuthStateChange`
 * callback, unlike `supabase.auth.getUser()` / `getSession()` which re-acquire
 * the auth client's lock and deadlock while it is recovering a stored session.
 */
const mapSupabaseUser = (user: SupabaseAuthUser): User => ({
  id: user.id,
  email: user.email || "",
  firstName: user.user_metadata?.first_name,
  lastName: user.user_metadata?.last_name,
  firstNameTH: user.user_metadata?.first_name_th,
  lastNameTH: user.user_metadata?.last_name_th,
  avatarUrl: user.user_metadata?.avatar_url,
  phone: user.user_metadata?.phone,
  language: user.user_metadata?.language || "th",
  timezone: user.user_metadata?.timezone || "Asia/Bangkok",
  emailVerified: user.email_confirmed_at != null,
  lastSignInAt: user.last_sign_in_at || undefined,
  createdAt: user.created_at,
  updatedAt: user.updated_at || user.created_at,
});

/**
 * Get current authenticated user.
 *
 * Calls `getUser()` (a network round-trip). Safe to call *after* the auth client
 * has initialised (e.g. from `refreshSession`), but NOT at
 * mount or inside an `onAuthStateChange` callback — see `mapSupabaseUser`.
 */
export const getCurrentUser = async (): Promise<User | null> => {
  const supabase = getSupabaseClient();

  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error || !user) {
    return null;
  }

  return mapSupabaseUser(user);
};

/**
 * Build the app `AuthSession` for an already-resolved user.
 *
 * Only runs auth-safe work — never an auth-lock method — so it is safe once the
 * auth client has finished initialising. When invoked from an
 * `onAuthStateChange` callback it must still be deferred (see `onAuthStateChange`).
 *
 * This used to query workspace_members and map each row 1:1 onto an
 * "Organization", because the parent monorepo's auth layer spoke that language.
 * Tenancy is WorkspaceContext's job, so the session is now just the user, and a
 * signed-in user with no workspace reaches the app and is told so, rather than
 * being bounced back to the sign-in page with no explanation.
 */
const buildSessionFromUser = (user: User): AuthSession => ({ user });

/**
 * Get the full auth session for the signed-in user.
 */
export const getAuthSession = async (): Promise<AuthSession | null> => {
  // Demo mode: hand back a mock user. Which workspace demo shows is
  // workspaceService's business, not this function's — it used to fabricate an
  // "organization" here from DEMO_CONFIG.workspaceId while workspaceService
  // fabricated a different one from DEMO_WORKSPACE, and the two never matched.
  const { DEMO_MODE, DEMO_CREDENTIALS } = await import("./demoConfig");
  if (DEMO_MODE) {
    const now = new Date().toISOString();
    return {
      user: {
        id: "demo-user-all-workspaces",
        email: DEMO_CREDENTIALS.email,
        firstName: "Demo",
        lastName: "User",
        language: "th",
        timezone: "Asia/Bangkok",
        emailVerified: true,
        createdAt: now,
        updatedAt: now,
      },
    };
  }

  const user = await getCurrentUser();
  if (!user) return null;

  return buildSessionFromUser(user);
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (
  email: string,
  password: string,
  t: TFn,
): Promise<{ success: boolean; error?: string }> => {
  // Only shortcut when there is no backend to ask. Both flags matter and they
  // are not the same flag: the demo credentials are worth accepting only if a
  // session can follow, and the session comes from DEMO_MODE (getAuthSession
  // returns the synthetic user under it, and AuthContext applies a null session
  // whenever Supabase is unconfigured). Keyed on !isSupabaseConfigured() alone,
  // an unconfigured build with demo mode off answered "success" to the demo
  // credentials, navigated to /campaigns, found no session, and bounced back to
  // this form with nothing on screen to explain it.
  //
  // When Supabase IS configured the real sign-in runs even under demo mode,
  // because the seeded scenario accounts (demoDataService DEMO_CONFIG.demoUser)
  // are real rows in that project and demos sign in with them.
  if (!isSupabaseConfigured()) {
    const { DEMO_MODE, DEMO_CREDENTIALS } = await import("./demoConfig");
    if (DEMO_MODE && email === DEMO_CREDENTIALS.email && password === DEMO_CREDENTIALS.password) {
      return { success: true };
    }
    return {
      success: false,
      error: DEMO_MODE
        ? t("auth.errDemoOnlyAccount")
        : t("auth.errNoSupabase"),
    };
  }

  const supabase = getSupabaseClient();

  const { error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
};

/**
 * Sign up with email and password
 * Also creates a workspace for the new user
 */
export const signUpWithEmail = async (
  email: string,
  password: string,
  t: TFn,
  metadata?: Record<string, unknown>,
): Promise<{
  success: boolean;
  error?: string;
  requiresVerification?: boolean;
}> => {
  // Account creation requires a real Supabase backend
  if (!isSupabaseConfigured()) {
    return {
      success: false,
      error: t("auth.errSignupDemo"),
    };
  }

  const supabase = getSupabaseClient();

  const redirectUrl = typeof window !== "undefined"
    ? `${window.location.origin}/auth/callback`
    : undefined;

  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: redirectUrl,
      },
    });

    if (error) {
      return { success: false, error: error.message };
    }

    // Check if email verification is required
    if (data.user && !data.session) {
      return { success: true, requiresVerification: true };
    }

    // The workspace is not created here. The on_user_created trigger on
    // auth.users does it — AFTER INSERT OR UPDATE, so it covers both the
    // auto-confirmed signup and the confirm-by-email-later path this function
    // cannot see, it reads workspace_name out of raw_user_meta_data (the
    // metadata passed above), and it returns early if the user already has a
    // membership, so it cannot double-create.
    //
    // This used to also call the create_user_workspace RPC from the browser,
    // after sleeping 500ms for the row to appear. That call could never have
    // worked: migration 20240101000012 revokes EXECUTE on it from anon and
    // authenticated, precisely so it is not reachable from a client. Every
    // auto-confirmed signup therefore paid the delay and logged a permission
    // error, while the workspace it was "failing" to create already existed.
    return { success: true };
  } catch (err) {
    logError('Supabase', 'Signup error', err);
    return { success: false, error: t("auth.errUnexpected") };
  }
};

/**
 * Sign out
 */
export const signOut = async (): Promise<void> => {
  const supabase = getSupabaseClient();
  await supabase.auth.signOut();

};

/**
 * Subscribe to auth state changes
 */
export const onAuthStateChange = (
  callback: (event: string, session: AuthSession | null) => void
) => {
  if (!isSupabaseConfigured()) {
    return { unsubscribe: () => {} };
  }

  const supabase = getSupabaseClient();

  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange((event, session) => {
    if (event === "SIGNED_OUT") {
      callback(event, null);
      return;
    }

    // Build the app session from the `session` object the client hands us —
    // NEVER by calling getUser()/getSession() here. Those re-acquire the auth
    // client's lock, which is held while this callback runs during the client's
    // init/session-recovery, and would deadlock (the "Loading..." hang).
    //
    // The setTimeout stays, but NOT for buildSessionFromUser's sake — that is
    // pure and synchronous now. It defers `callback`, whose consumers reach for
    // the lock the moment they hear about a session: AuthContext setState ->
    // render -> WorkspaceProvider mounts -> getUserWorkspaces() ->
    // supabase.auth.getUser() (workspaceService.ts:76,138). Firing the callback
    // inline invites that chain to run while the lock is still held. Deleting
    // this because the old comment referred to queries that no longer exist
    // would restore the exact hang 0a8a6dd fixed.
    setTimeout(() => {
      void (async () => {
        try {
          const appSession = session?.user
            ? buildSessionFromUser(mapSupabaseUser(session.user))
            : null;
          callback(event, appSession);
        } catch (error) {
          logError('Supabase', 'Error building auth session on state change', error);
          callback(event, null);
        }
      })();
    }, 0);
  });

  return subscription;
};

export const getSupabaseConfig = () => ({
  url: SUPABASE_URL,
  hasKey: !!SUPABASE_ANON_KEY,
  configured: isSupabaseConfigured(),
});

/**
 * แก้ชื่อที่แสดงของตัวเอง
 *
 * ต้องอยู่ใต้ `data` — mapSupabaseUser (ข้างบนไฟล์นี้) อ่านจาก
 * `user_metadata.first_name` การส่งระดับบนสุดจะสำเร็จแบบไม่มีอะไรเกิดขึ้น
 *
 * ส่งทั้งสอง field เสมอแม้ค่าจะว่าง: GoTrue **merge** user_metadata (พิสูจน์กับ
 * GoTrue จริงแล้ว — ยิง data ที่มีแค่ last_name แล้ว first_name/workspace_name รอด)
 * ดังนั้น field ที่ไม่ส่งจะคงค่าเดิม ไม่ได้ถูกลบ ถ้าคนลบนามสกุลจนว่างแล้วเราละไว้
 * นามสกุลเดิมจะยังอยู่ใน DB และหน้าจะโกหกว่าลบสำเร็จ
 */
export const updateDisplayName = async (
  firstName: string,
  lastName: string,
  t: TFn,
): Promise<ServiceResult<void>> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: t("auth.errDemoMode") };
  }

  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.updateUser({
      data: { first_name: firstName, last_name: lastName },
    });

    if (error) {
      logError("Supabase", "Failed to update display name", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    logError("Supabase", "Failed to update display name", err);
    return { success: false, error: t("auth.errUnexpected") };
  }
};

/**
 * เปลี่ยนรหัสผ่านของตัวเอง
 *
 * ลำดับนี้พิสูจน์กับ GoTrue 2.189.0 จริงแล้วทั้งเส้น (spec §2.3):
 *
 * 1. ยืนยันรหัสเดิม — `secure_password_change = false` และ docker-compose ไม่ได้ตั้ง
 *    reauthentication env แปลว่า updateUser({password}) เปลี่ยนรหัสให้ทันทีโดยไม่ถาม
 *    การถามรหัสเดิมจึงเป็นสิ่งที่ "เรา" ทำ ไม่ใช่สิ่งที่ Supabase ทำให้
 *    signInWithPassword ที่ fail **ไม่ล้าง session เดิม** (พิสูจน์แล้ว) — พิมพ์ผิดจึงไม่โดนเตะออก
 * 2. เปลี่ยนรหัส
 * 3. เตะ session อื่น — ถ้าไม่เตะ session ที่ถูกขโมยยังใช้ได้ การถามรหัสเดิมก็กันได้แค่ครึ่งเดียว
 *    ขั้นนี้ fail ไม่ใช่ความล้มเหลวของการเปลี่ยนรหัส เพราะรหัสเปลี่ยนไปแล้วจริง
 */
export const changePassword = async (
  currentPassword: string,
  newPassword: string,
  t: TFn,
): Promise<ServiceResult<void>> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: t("auth.errDemoMode") };
  }

  try {
    const supabase = getSupabaseClient();

    const { data: sessionData } = await supabase.auth.getSession();
    const email = sessionData.session?.user.email;
    if (!email) {
      return { success: false, error: t("auth.errNotSignedIn") };
    }

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password: currentPassword,
    });
    if (signInError) {
      // GoTrue ว่า "Invalid login credentials" ซึ่งไม่บอกว่าผิดช่องไหน — ที่นี่มีช่องเดียว
      // ที่ผิดได้ จึงบอกให้ตรง
      return { success: false, error: t("auth.errWrongCurrentPassword") };
    }

    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
    if (updateError) {
      // ข้อความของ GoTrue อ่านรู้เรื่องอยู่แล้ว เช่น
      // "New password should be different from the old password." — ส่งต่อตรง ๆ
      logError("Supabase", "Failed to change password", updateError);
      return { success: false, error: updateError.message };
    }

    const { error: signOutError } = await supabase.auth.signOut({ scope: "others" });
    if (signOutError) {
      // รหัส**เปลี่ยนไปแล้ว** — บอกว่าล้มเหลวจะทำให้คนลองใหม่ด้วยรหัสเดิมที่ใช้ไม่ได้แล้ว
      logError("Supabase", "Password changed but could not sign out other sessions", signOutError);
      return {
        success: true,
        error: t("auth.errChangedButSignOutFailed"),
      };
    }

    return { success: true };
  } catch (err) {
    logError("Supabase", "Change password error", err);
    return { success: false, error: t("auth.errUnexpected") };
  }
};

/**
 * ขอลิงก์ตั้งรหัสผ่านใหม่ทางอีเมล
 *
 * **ตอบเหมือนกันเสมอ ไม่ว่าอีเมลนั้นจะมีอยู่จริงหรือไม่** — นี่คือทั้งหมดที่ฟังก์ชันนี้
 * ต้องระวัง ถ้าตอบต่างกันหน้านี้จะกลายเป็นเครื่องมือให้คนนอกไล่เช็กว่าใครเป็นผู้ใช้ของ
 * ระบบนี้บ้าง โดยไม่ต้องมีรหัสผ่านสักตัว
 *
 * error ทุกชนิดจึงถูกกลืน แล้วบันทึกลง log แทน — รวมถึง 429 ของ GoTrue ที่จำกัดการส่ง
 * ต่อหนึ่งอีเมลด้วย เพราะ "อีเมลนี้ถูกจำกัด" ก็บอกเป็นนัยว่าอีเมลนี้มีอยู่จริง
 * ผลข้างเคียงที่ยอมรับ: ถ้า SMTP พังจริง ผู้ใช้จะเห็นข้อความเดิม — log คือที่เดียว
 * ที่บอกได้ว่าเมลไม่ได้ออก
 *
 * @param email - อีเมลที่ผู้ใช้กรอก
 * @param redirectTo - URL ที่ลิงก์ในเมลจะพากลับมา ต้องอยู่ใน GOTRUE_URI_ALLOW_LIST
 *   ไม่งั้น GoTrue จะ **เงียบ ๆ** ตัดทิ้งแล้วส่งกลับไปที่ SITE_URL แทน
 * @returns success เสมอ
 */
export const requestPasswordReset = async (
  email: string,
  redirectTo: string
): Promise<ServiceResult<void>> => {
  if (!isSupabaseConfigured()) {
    return { success: true };
  }

  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) {
      logError("Supabase", "Password reset request failed", error);
    }
  } catch (err) {
    logError("Supabase", "Password reset request error", err);
  }

  return { success: true };
};

/**
 * ตั้งรหัสผ่านใหม่ ด้วย session ที่ได้จากลิงก์ในอีเมล
 *
 * ต่างจาก changePassword ตรงที่ไม่ถามรหัสเดิม — คนที่มาถึงตรงนี้คือคนที่จำรหัสเดิมไม่ได้
 * สิ่งที่ยืนยันตัวตนแทนคือการที่เขาเปิดอีเมลนั้นได้
 *
 * เตะ **ทุก** session ไม่ใช่แค่เครื่องอื่น (changePassword ใช้ "others") เพราะเหตุผลที่คน
 * มารีเซ็ตมักคือสงสัยว่ารหัสหลุด การปล่อยให้ session ไหนรอดไว้ก็เท่ากับรีเซ็ตไม่จริง
 * และนั่นแปลว่าเครื่องที่เพิ่งตั้งรหัสเสร็จก็ถูกเตะด้วย ต้องล็อกอินใหม่ — ตั้งใจให้เป็น
 * แบบนั้น หน้าจอต้องพาไปหน้า signin ต่อ
 *
 * ขั้นเตะ session ที่ fail **ไม่ใช่** ความล้มเหลวของการตั้งรหัส เพราะรหัสเปลี่ยนไปแล้วจริง
 * บอกว่า fail จะทำให้คนลองใหม่ด้วยรหัสเดิมที่ใช้ไม่ได้แล้ว (เหตุผลเดียวกับ changePassword)
 *
 * @param newPassword - รหัสผ่านใหม่
 * @returns success เมื่อรหัสเปลี่ยนแล้ว แม้การเตะ session จะล้มเหลว
 */
export const completePasswordReset = async (
  newPassword: string,
  t: TFn,
): Promise<ServiceResult<void>> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: t("auth.errDemoMode") };
  }

  try {
    const supabase = getSupabaseClient();

    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
    if (updateError) {
      // ลิงก์หมดอายุ/ถูกใช้ไปแล้ว จะโผล่ที่นี่ ข้อความของ GoTrue อ่านรู้เรื่อง ส่งต่อตรง ๆ
      logError("Supabase", "Failed to complete password reset", updateError);
      return { success: false, error: updateError.message };
    }

    const { error: signOutError } = await supabase.auth.signOut({ scope: "global" });
    if (signOutError) {
      logError("Supabase", "Password reset but could not sign out sessions", signOutError);
      return {
        success: true,
        error: t("auth.errUpdatedButSignOutFailed"),
      };
    }

    return { success: true };
  } catch (err) {
    logError("Supabase", "Complete password reset error", err);
    return { success: false, error: t("auth.errUnexpected") };
  }
};

export default getSupabaseClient;
